/**
 * FIT base types. The base-type byte carried in a field definition is
 * `(endianAbility << 7) | typeNumber`, where the low 5 bits are the type number.
 *
 * Design decision (see IMPLEMENTATION_PLAN.md "Codec"): only the integer base types
 * are decoded to JS numbers. Everything else — string, byte, both floats, the 64-bit
 * integers and any unknown type — is kept as an opaque `Uint8Array` of exactly `size`
 * bytes. This is what makes re-serialisation byte-identical: a float32 NaN (0xFFFFFFFF,
 * the "invalid" sentinel) would not survive a double round-trip through `DataView`, and
 * we never need to read a float or a 64-bit value anyway.
 */
export interface BaseTypeInfo {
  readonly name: string
  readonly typeNumber: number
  /** Bytes per element. */
  readonly size: number
  /** Decoded to a number/number[] (true) or kept as opaque bytes (false). */
  readonly integer: boolean
  readonly signed: boolean
}

const T = (
  typeNumber: number,
  name: string,
  size: number,
  integer: boolean,
  signed: boolean,
): BaseTypeInfo => ({ name, typeNumber, size, integer, signed })

/** Indexed by base-type number (the low 5 bits of the base-type byte). */
const BY_NUMBER: readonly (BaseTypeInfo | undefined)[] = [
  T(0, 'enum', 1, true, false),
  T(1, 'sint8', 1, true, true),
  T(2, 'uint8', 1, true, false),
  T(3, 'sint16', 2, true, true),
  T(4, 'uint16', 2, true, false),
  T(5, 'sint32', 4, true, true),
  T(6, 'uint32', 4, true, false),
  T(7, 'string', 1, false, false),
  T(8, 'float32', 4, false, false),
  T(9, 'float64', 8, false, false),
  T(10, 'uint8z', 1, true, false),
  T(11, 'uint16z', 2, true, false),
  T(12, 'uint32z', 4, true, false),
  T(13, 'byte', 1, false, false),
  T(14, 'sint64', 8, false, true),
  T(15, 'uint64', 8, false, false),
  T(16, 'uint64z', 8, false, false),
]

/**
 * Resolve a base-type byte. Unknown type numbers (a FIT profile can introduce more) are
 * treated as opaque single bytes so they pass through untouched rather than throwing.
 */
export function baseTypeFromByte(byte: number): BaseTypeInfo {
  const info = BY_NUMBER[byte & 0x1f]
  if (info) return info
  return T(byte & 0x1f, `unknown_0x${(byte & 0x1f).toString(16)}`, 1, false, false)
}
